/**
 * Модуль динамического разрешения URL-адресов API бэкенда
 *
 * Определяет пул gRPC URL в рантайме на основе списка поддерживаемых доменов
 * и текущего хоста приложения для отказоустойчивого переключения (failover).
 *
 * Схема:
 * - dev:  grpc.dev.<baseDomain>
 * - prod: grpc.<baseDomain>
 */

import { ref } from 'vue'
import type { IApiUrls } from '@/modules/api/types/IApiUrls'
import { IS_DEV_BUILD } from '@/modules/constants/buildMode'

/**
 * Основной базовый домен проекта (к которому привязана авторизация Telegram Login Widget)
 */
export const PRIMARY_BASE_DOMAIN = 'digital-security.pro'

/**
 * Резервные (fallback) базовые домены проекта
 */
export const FALLBACK_BASE_DOMAINS: string[] = [
  'digitalsecurity001.ru',
]

/**
 * Извлекает базовый домен (2-й и 1-й уровни) из полного hostname
 *
 * @param hostname - полный hostname (например, app.dev.mothrfkr.com)
 * @returns базовый домен (например, mothrfkr.com)
 */
export function extractBaseDomain(hostname: string): string {
  // 1. Разбиваем hostname на сегменты по точке
  const parts = hostname.split('.')
  // 2. Берём последние 2 сегмента — домен 2-го и 1-го уровня
  return parts.slice(-2).join('.')
}

/**
 * Определяет, является ли hostname локальным адресом
 * (localhost, loopback, приватные IP-адреса)
 *
 * @param hostname - hostname для проверки
 * @returns true если hostname является локальным адресом
 */
export function isLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '::1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.')
  )
}


/**
 * Формирует gRPC URL для указанного базового домена с учетом режима сборки
 *
 * @param domain - базовый домен (например, digital-security.pro)
 * @returns полный URL gRPC
 */
export function buildGrpcUrlForDomain(domain: string): string {
  const envPrefix = IS_DEV_BUILD ? '.dev.' : '.'
  return `https://grpc${envPrefix}${domain}`
}

/**
 * Разрешает упорядоченный список gRPC URL (основной первым, далее резервные)
 *
 * @returns массив URL-адресов gRPC серверов
 */
export function resolveGrpcUrls(): string[] {
  if (import.meta.env.DEV) {
    const primaryUrl = (import.meta.env.VITE_APP_API_BASE_GRPC as string) || buildGrpcUrlForDomain(PRIMARY_BASE_DOMAIN)
    const fallbackUrls = FALLBACK_BASE_DOMAINS.map(buildGrpcUrlForDomain).filter(url => url !== primaryUrl)
    return [primaryUrl, ...fallbackUrls]
  }

  const hostname = window.location.hostname

  if (isLocalhost(hostname)) {
    const primaryUrl = (import.meta.env.VITE_APP_API_BASE_GRPC as string) || buildGrpcUrlForDomain(PRIMARY_BASE_DOMAIN)
    const fallbackUrls = FALLBACK_BASE_DOMAINS.map(buildGrpcUrlForDomain).filter(url => url !== primaryUrl)
    return [primaryUrl, ...fallbackUrls]
  }

  const currentBaseDomain = extractBaseDomain(hostname)
  const currentGrpcUrl = buildGrpcUrlForDomain(currentBaseDomain)

  const allKnownDomains = [PRIMARY_BASE_DOMAIN, ...FALLBACK_BASE_DOMAINS]
  const otherUrls = allKnownDomains
    .filter(domain => domain !== currentBaseDomain)
    .map(buildGrpcUrlForDomain)

  return [currentGrpcUrl, ...otherUrls]
}

const initialUrls = resolveGrpcUrls()

/** Текущий активный gRPC URL */
export const activeGrpcUrl = ref<string>(initialUrls[0] || '')

/**
 * Признак активности резервного (fallback) домена.
 * true если текущий активный адрес не относится к основному домену.
 */
export const isFallbackActive = ref<boolean>(
  !initialUrls[0]?.includes(PRIMARY_BASE_DOMAIN)
)

/**
 * Переключает активный gRPC URL на указанный адрес
 *
 * @param url - новый целевой URL
 */
export function setActiveGrpcUrl(url: string): void {
  activeGrpcUrl.value = url
  const fallback = !url.includes(PRIMARY_BASE_DOMAIN)
  isFallbackActive.value = fallback

  if (fallback) {
    try {
      import('@/modules/auth/stores/authStore').then(({ useAuthStore }) => {
        const authStore = useAuthStore()
        authStore.isTelegramLoginAvailable = false
      })
    } catch {
      // Pinia еще не инициализирована
    }
  }

  console.log('[resolveApiUrls] Активный gRPC URL переключен:', {
    activeGrpcUrl: url,
    isFallbackActive: isFallbackActive.value,
  })
}

/**
 * Разрешает URL-адреса API бэкенда для совместимости
 *
 * @returns объект IApiUrls с полями grpcUrl и restUrl
 */
export function resolveApiUrls(): IApiUrls {
  const grpcUrl = activeGrpcUrl.value || initialUrls[0] || ''
  const restUrl = grpcUrl.replace('grpc.', 'rest.')
  return { grpcUrl, restUrl }
}
